import { useEffect, useRef } from "react";
import { useU2000WebSocketContext } from "../u2000Core/context/websocket/U2000WebSocketContext";
import {
  isU3121GoogleCalendarSyncMessage,
  U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT,
  type U3121GoogleCalendarSyncWsFormUpdate,
} from "../u2000Core/types/U2000WebSocketMessages";
import type { U3121GoogleCalendarSync } from "./U3121Service";

export type U3121GoogleCalendarSyncNotificationOptions = {
  projectId: string | undefined;
  /** Absent ou null = fiche projet ; renseigné = fiche sous-projet. */
  subProjectId?: string | null;
  onSyncUpdated: (update: U3121GoogleCalendarSyncWsFormUpdate) => void;
};

function mergeGoogleCalendarSyncFromWs(
  sync: U3121GoogleCalendarSyncWsFormUpdate["googleCalendarSync"],
  dateDivergence: U3121GoogleCalendarSyncWsFormUpdate["dateDivergence"],
  previous?: U3121GoogleCalendarSync | null,
): U3121GoogleCalendarSync | null {
  const prevDiv = previous?.dateDivergence;

  if (!sync) {
    if (dateDivergence === undefined && !prevDiv) return previous ?? null;
    if (dateDivergence === undefined) return previous ?? null;
    if (dateDivergence === null) {
      if (!previous) return null;
      const next = { ...previous };
      delete next.dateDivergence;
      return next;
    }
    return { ...(previous ?? {}), dateDivergence };
  }

  const merged: U3121GoogleCalendarSync = { ...sync };
  if (dateDivergence !== undefined) {
    if (dateDivergence === null) {
      delete merged.dateDivergence;
    } else {
      merged.dateDivergence = dateDivergence;
    }
  } else if (prevDiv) {
    merged.dateDivergence = prevDiv;
  }
  return merged;
}

/**
 * Écoute l'événement WebSocket dédié U3121 `u3121:googleCalendarSync` (hors centre U2105).
 * Met à jour `projectToSurvey.googleCalendarSync` (statut + écart date BL-0629) pour le même projet / sous-projet.
 */
export function useU3121GoogleCalendarSyncNotification(options: U3121GoogleCalendarSyncNotificationOptions): void {
  const { projectId, subProjectId, onSyncUpdated } = options;
  const { on, off } = useU2000WebSocketContext();
  const onSyncUpdatedRef = useRef(onSyncUpdated);
  onSyncUpdatedRef.current = onSyncUpdated;

  useEffect(() => {
    if (!projectId) return;

    const handleMessage = (message: unknown) => {
      if (!isU3121GoogleCalendarSyncMessage(message)) return;
      if (message.projectId !== projectId) return;

      const messageSubProjectId = message.subProjectId ?? null;
      const expectedSubProjectId = subProjectId ?? null;
      if (messageSubProjectId !== expectedSubProjectId) return;

      const dateDivergence = "dateDivergence" in message ? message.dateDivergence : undefined;
      onSyncUpdatedRef.current({
        googleCalendarSync: message.googleCalendarSync,
        dateDivergence,
        projectDueDate: message.projectDueDate,
      });
    };

    on(U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT, handleMessage);
    return () => off(U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT, handleMessage);
  }, [projectId, subProjectId, on, off]);
}

export { mergeGoogleCalendarSyncFromWs };
